import { useState, HTMLAttributes } from "react";
import { cn } from "@/utils/cn";
import { CheckCircle, SparklesIcon } from "lucide-react";

interface TabItem {
  id: string;
  title: string;
  number?: string;
}

interface CertificationItem {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

interface TabSectionProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  description?: string;
  tag?: string;
  tagHref?: string;
  tabs: TabItem[];
  tabContent: Record<string, {
    title: string;
    description: string;
    certifications: CertificationItem[];
  }>;
}

export const TabSection = ({
  title = "Compliance and Certifications",
  subtitle = "Built to meet your security needs",
  description = "Cobalt is built with features to meet and exceed your enterprise security and privacy needs",
  tag = "Enterprise-level security & privacy",
  tagHref = "/security",
  tabs,
  tabContent,
  className,
  ...props
}: TabSectionProps) => {
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.id || "");

  return (
    <section className={cn("pt-24 pb-8", className)} {...props}>
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Tag */}
        {tag && (
          <div className="flex justify-center mb-4">
            <span
              className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 md:px-4 md:py-1.5 text-xs md:text-sm font-medium text-primary-600 transition-colors"
            >
              <span className="relative">{tag}</span>
            </span>
          </div>
        )}

        {/* Header */}
        {title && (
          <div className="text-center max-w-4xl mx-auto mb-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">{subtitle}</h2>
            {description && <p className="text-lg text-gray-600 mb-16">{description}</p>}
          </div>
        )}

        {/* Desktop View */}
        <div className="hidden lg:flex lg:flex-row gap-8 max-w-6xl mx-auto">
          {/* Desktop Sidebar Tabs */}
          <div className="lg:w-2/5 lg:border-r border-gray-200 pr-6">
            <nav className="flex flex-col space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "text-left px-4 py-3 border-l-4 transition-all duration-200",
                    activeTab === tab.id
                      ? "border-indigo-600 text-indigo-600 font-medium"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  )}
                >
                  {tab.title}
                </button>
              ))}
            </nav>
          </div>

          {/* Desktop Content Area */}
          <div className="lg:w-3/5">
            {activeTab && tabContent[activeTab] && (
              <div>
                <h3 className="text-2xl font-bold mb-4">{tabContent[activeTab].title}</h3>
                <p className="text-gray-600 mb-8">{tabContent[activeTab].description}</p>

                {/* Desktop Certifications List */}
                <div className="space-y-8">
                  {tabContent[activeTab].certifications.map((cert, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="text-green-500 mt-1 flex-shrink-0">
                        {cert.icon || <CheckCircle size={24} />}
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg mb-1">{cert.title}</h4>
                        <p className="text-gray-600">{cert.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile View - Vertical Stacked Sections */}
        <div className="lg:hidden">
          {tabs.map((tab, index) => (
            <div
              key={tab.id}
              id={`mobile-tab-${tab.id}`}
              className="relative mb-16"
            >
              {/* Mobile Tab Header */}
              <div className="mb-6">
                <div className="border bg-primary-50 text-primary-600 px-3 py-3 rounded-lg border-primary-400">
                  <div className="flex flex-row justify-between items-center">
                    <div className="text-sm font-bold flex items-center gap-2">
                      <SparklesIcon className="w-4 h-4" /> {tab.title.toUpperCase()}
                    </div>
                    <div className="text-sm font-bold">
                      {tab.number || `0${index + 1}.`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Tab Content */}
              <div className="text-left px-4">
                <p className="text-gray-600 mb-8">{tabContent[tab.id].description}</p>

                {/* Features List */}
                <div className="space-y-8">
                  {tabContent[tab.id].certifications.map((cert, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="text-indigo-600 flex-shrink-0 mt-1">
                        <SparklesIcon className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg mb-2">{cert.title}</h4>
                        {cert.description && <p className="text-gray-600">{cert.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TabSection;